import { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { entitiesSelectors, fetchStudents } from '@/store/reducers/entities';
import { StudentReportItem } from '@/school-management/types/response';

interface UseStudentsOptions {
  enabled?: boolean;
}

interface UseStudentsResult {
  students: StudentReportItem[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useStudents(
  organizationIdOverride?: string,
  options?: UseStudentsOptions
): UseStudentsResult {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const studentsState = useAppSelector(entitiesSelectors.students);
  const enabled = options?.enabled !== false;

  const organizationId = useMemo(() => {
    if (organizationIdOverride) return organizationIdOverride;
    return user?.metadata?.organizationDetails?.organizationId;
  }, [organizationIdOverride, user?.metadata?.organizationDetails?.organizationId]);

  useEffect(() => {
    if (!organizationId || !enabled) return;

    const shouldFetch =
      !studentsState.lastFetched ||
      studentsState.organizationId !== organizationId ||
      studentsState.error !== null;

    if (shouldFetch && !studentsState.isLoading) {
      dispatch(fetchStudents(organizationId));
    }
  }, [
    organizationId,
    enabled,
    dispatch,
    studentsState.lastFetched,
    studentsState.organizationId,
    studentsState.error,
    studentsState.isLoading,
  ]);

  const reload = async () => {
    if (!organizationId) return;
    await dispatch(fetchStudents(organizationId));
  };

  return {
    students: studentsState.items,
    isLoading: studentsState.isLoading,
    error: studentsState.error,
    reload,
  };
}
